/**
 * ANORDNUNG DER GUIDE-SPALTE — je Person, am Server gemerkt.
 *
 * Rafael, 25.09.2026 10:22Z, fuer alle Nutzer (Partner wie ihn selbst): vier
 * Anordnungen — Guide und Chat nebeneinander, uebereinander (die bisherige
 * Vorgabe), nur Guide, nur Chat. Wahl und Groessen liegen je Person am Server,
 * damit sie auf jedem Geraet gleich sind. Anlass: Kurt hat zwei Bildschirme; auf
 * einem war fuer Guide oben / Chat unten kein Platz.
 *
 * Diese Datei ist rein (kein DOM, kein fs): Client und Server pruefen mit
 * denselben Grenzen, der Server zusaetzlich mit zod.
 */

export const GUIDE_ANORDNUNG_MODI = ['nebeneinander', 'uebereinander', 'nurGuide', 'nurChat'] as const;
export type GuideAnordnungModus = (typeof GUIDE_ANORDNUNG_MODI)[number];

/** Alle Groessen in Prozent. Grenzen sind dieselben, die die Griffe beim Ziehen einhalten. */
export const GUIDE_ANORDNUNG_GRENZEN = {
  /**
   * Breite der Spalte am Fenster — uebereinander, nur Guide, nur Chat.
   * Rafael, 03.10.2026 10:15: „Guide-Spalte: Vorgabe 18 % statt 26 % (dein eigener Wert),
   * schmalste Breite 15 %, aber nie unter 420 px. Deine gespeicherte Breite bleibt."
   * Die 420 px stehen unten (GUIDE_SPALTE_MINDEST_PX), nicht hier — hier ist alles Prozent.
   */
  guideBreite: { min: 15, max: 55, vorgabe: 18 },
  /** Breite der Spalte am Fenster — nebeneinander braucht mehr Platz. */
  breiteNebeneinander: { min: 30, max: 85, vorgabe: 52 },
  /** Anteil des Guide an der Spaltenhoehe — uebereinander. */
  guideHoehe: { min: 25, max: 85, vorgabe: 60 },
  /** Anteil des Guide an der Spaltenbreite — nebeneinander. */
  guideAnteil: { min: 20, max: 80, vorgabe: 50 },
} as const;

export type GuideAnordnungGroesse = keyof typeof GUIDE_ANORDNUNG_GRENZEN;

/**
 * Untergrenze der Spalte in Pixeln: die Spalte ist effektiv max(420 px, Prozentwert).
 * Gilt nur in breiten Fenstern (ab GUIDE_SPALTE_MINDEST_AB_PX Fensterbreite) — darunter
 * (Tablet, kleine Laptops, ueberlagernde Spalte unter 1100 px) nimmt sie sonst der Buehne
 * den Platz. Dieselben Zahlen stehen in `src/styles/responsive.css` (Media-Query).
 */
export const GUIDE_SPALTE_MINDEST_PX = 420;
export const GUIDE_SPALTE_MINDEST_AB_PX = 1400;

export interface GuideAnordnung {
  modus: GuideAnordnungModus;
  guideBreite: number;
  breiteNebeneinander: number;
  guideHoehe: number;
  guideAnteil: number;
}

/** Wer nichts gespeichert hat: uebereinander, 18 % (mind. 420 px) / 60 %. Bis 03.10.2026 waren es 26 %. */
export const GUIDE_ANORDNUNG_VORGABE: GuideAnordnung = {
  modus: 'uebereinander',
  guideBreite: GUIDE_ANORDNUNG_GRENZEN.guideBreite.vorgabe,
  breiteNebeneinander: GUIDE_ANORDNUNG_GRENZEN.breiteNebeneinander.vorgabe,
  guideHoehe: GUIDE_ANORDNUNG_GRENZEN.guideHoehe.vorgabe,
  guideAnteil: GUIDE_ANORDNUNG_GRENZEN.guideAnteil.vorgabe,
};

export function istGuideAnordnungModus(v: unknown): v is GuideAnordnungModus {
  return typeof v === 'string' && (GUIDE_ANORDNUNG_MODI as readonly string[]).includes(v);
}

/** Eine Groesse auf ihre Grenzen setzen; Unbrauchbares ist ein Fehler, keine stille Vorgabe. */
export function begrenzeGroesse(feld: GuideAnordnungGroesse, wert: number): number {
  if (typeof wert !== 'number' || !Number.isFinite(wert)) throw new Error(`${feld}: keine endliche Zahl (${String(wert)})`);
  const g = GUIDE_ANORDNUNG_GRENZEN[feld];
  return Math.round(Math.max(g.min, Math.min(g.max, wert)) * 10) / 10;
}

/**
 * Liest einen Stand aus dem Zwischenspeicher des Browsers. Hier (und NUR hier) wird
 * Unbrauchbares verworfen statt geworfen: der Zwischenspeicher ist regenerierbar, der
 * Server hat das letzte Wort. Liefert `null`, wenn nichts Brauchbares dasteht — der
 * Aufrufer entscheidet, ob er die Vorgabe nimmt.
 */
export function leseGuideAnordnung(roh: unknown): GuideAnordnung | null {
  if (!roh || typeof roh !== 'object') return null;
  const o = roh as Record<string, unknown>;
  if (!istGuideAnordnungModus(o.modus)) return null;
  const aus: GuideAnordnung = { ...GUIDE_ANORDNUNG_VORGABE, modus: o.modus };
  for (const feld of Object.keys(GUIDE_ANORDNUNG_GRENZEN) as GuideAnordnungGroesse[]) {
    const v = o[feld];
    if (typeof v === 'number' && Number.isFinite(v)) aus[feld] = begrenzeGroesse(feld, v);
  }
  return aus;
}

export type GuideAnordnungAktion =
  | { typ: 'modus'; modus: GuideAnordnungModus }
  | { typ: 'groesse'; feld: GuideAnordnungGroesse; wert: number }
  | { typ: 'vorgabe' }
  | { typ: 'vomServer'; stand: GuideAnordnung };

/** Reiner Zustandsuebergang der Anordnung. */
export function guideAnordnungReduzieren(stand: GuideAnordnung, a: GuideAnordnungAktion): GuideAnordnung {
  switch (a.typ) {
    case 'modus':
      if (!istGuideAnordnungModus(a.modus)) throw new Error(`Unbekannte Anordnung: ${String(a.modus)}`);
      return stand.modus === a.modus ? stand : { ...stand, modus: a.modus };
    case 'groesse': {
      const wert = begrenzeGroesse(a.feld, a.wert);
      return stand[a.feld] === wert ? stand : { ...stand, [a.feld]: wert };
    }
    case 'vorgabe':
      return { ...GUIDE_ANORDNUNG_VORGABE };
    case 'vomServer':
      return { ...a.stand };
  }
}

/** Was die Anordnung sichtbar macht. Eine Stelle, damit Anzeige und Tests dasselbe meinen. */
export function guideAnordnungSicht(modus: GuideAnordnungModus): { guide: boolean; chat: boolean; richtung: 'zeile' | 'spalte' } {
  switch (modus) {
    case 'nebeneinander': return { guide: true, chat: true, richtung: 'zeile' };
    case 'uebereinander': return { guide: true, chat: true, richtung: 'spalte' };
    case 'nurGuide': return { guide: true, chat: false, richtung: 'spalte' };
    case 'nurChat': return { guide: false, chat: true, richtung: 'spalte' };
  }
}
